import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridDateComparator, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NegotiatedQuoteLineItemGridRow } from '../../NegotiatedQuoteWidget.types';
import { NegotiationCaseQuoteStatusCell } from '@/legacy/components/DataGridCustom/cells/NegotiationCaseQuoteStatusCell/NegotiationCaseQuoteStatusCell';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  dateFormatterFactory,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { CuriosityLinkCell } from '~/components/DataGridCustom/cells/CuriosityLinkCell/CuriosityLinkCell';
import { CuriosityLocationCell } from '~/components/DataGridCustom/cells/CuriosityLocationCell/CuriosityLocationCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { QuoteLineItemGridField } from '~/modules/NegotiationQuoteDetailsWidget/NegotiationQuoteDetailsWidget.types';
import { RouteId } from '~/routes';

export const useNegotiatedQuoteDetailsDataGridConfig: UseDataGridConfig<NegotiatedQuoteLineItemGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.common, { keyPrefix: 'NegotiatedQuoteDataGrid.column' });
  const { features } = useConfig();
  return useMemo<DataGridConfig<NegotiatedQuoteLineItemGridRow>>(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    return {
      columnVisibilityModel: {
        [QuoteLineItemGridField.ORGANIZATION]: features.showOrganization,
        [QuoteLineItemGridField.DELIVERY_LOCATION]: features.showDeliveryLocation,
        [QuoteLineItemGridField.STATUS]: features.enableNegotiationQuoteLineItemBidding,
      },
      columns: [
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        createColumn({
          field: QuoteLineItemGridField.POSITION,
          minWidth: 70,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.TITLE,
          minWidth: 400,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.UNIT,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.QUANTITY,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            return row.quantity;
          },
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            isMasked: row.isMasked,
            showApproximationTooltip: true,
            valueStrikeThrough: row.pricePerUnit,
            value: row.discountedPricePerUnit,
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.COMMODITY,
          minWidth: 200,
          renderCell: cellFactory(CuriosityLinkCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            ns: CuriosityNamespaceType.COMMODITY,
            needs: {
              permission: Permission.BaseCommodityList,
            },
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.ORGANIZATION,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.DELIVERY_LOCATION,
          minWidth: 325,
          renderCell: cellFactory(CuriosityLocationCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.FROM,
          valueGetter: (_value, row) => row.shipmentTerms?.earlyDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
        createColumn({
          field: QuoteLineItemGridField.TO,
          valueGetter: (_value, row) => row.shipmentTerms?.lateDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
        }),
        createColumn({
          field: QuoteLineItemGridField.STATUS,
          minWidth: 150,
          editable: true,
          type: 'boolean',
          renderCell: cellFactory(NegotiationCaseQuoteStatusCell, () => ({
            viewOnly: true,
          })),
        }),
      ],
    } as DataGridConfig<NegotiatedQuoteLineItemGridRow>;
  }, [
    features.showDeliveryLocation,
    features.showOrganization,
    features.enableNegotiationQuoteLineItemBidding,
    t,
    tColumn,
  ]);
};
